import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, Check, AlertCircle, ThumbsUp } from 'lucide-react';
import { visibleInterval } from '@/lib/visibleInterval';

/** Top-nav notification bell. Polls /notifications/me every 30s. */
export default function NotificationBell() {
  const { api, user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!user?.email) return;
    try {
      const { data } = await api.get('/notifications/me');
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, [api, user]);

  useEffect(() => {
    load();
    return visibleInterval(load, 30000);
  }, [load]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try { await api.post('/notifications/mark-all-read'); load(); } catch { /* ignore */ }
  };

  const iconFor = (kind) => kind === 'approval_approved'
    ? <ThumbsUp size={12} className="text-emerald-600" />
    : <AlertCircle size={12} className="text-amber-600" />;

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-md hover:bg-slate-100 text-slate-600"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
          >{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="notification-bell-panel" className="absolute right-0 mt-2 w-80 bg-white border border-[#E2E2DF] rounded-xl shadow-xl overflow-hidden z-50">
          <header className="flex items-center justify-between px-4 py-2 border-b border-[#E2E2DF]">
            <p className="text-sm font-bold text-slate-800">Notifications</p>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#D35400] hover:text-[#A04000]">Mark all read</button>
            )}
          </header>
          <div className="max-h-80 overflow-y-auto divide-y divide-[#F0EFEC]">
            {items.length === 0 ? (
              <p className="p-6 text-center text-xs text-slate-400">No notifications yet.</p>
            ) : items.map(n => (
              <button
                key={n._id}
                data-testid={`notification-item-${n._id}`}
                onClick={() => { markRead(n._id); if (n.link_url) window.location.assign(n.link_url); }}
                className={`w-full text-left p-3 hover:bg-slate-50 ${!n.read ? 'bg-amber-50/40' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5">{iconFor(n.kind)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{n.title}</p>
                    <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{n.message}</p>
                  </div>
                  {!n.read && <Check size={11} className="text-slate-400 mt-1" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
